import { canEditMap } from './permissions';
import { canEditDoc, type ResponsibilityState } from './responsibility';

/**
 * ONE EDIT MODE (V0.53.5) — the questions every surface asks about it, written down once.
 *
 * Until V0.53.5 the app had two flags that both meant "editing": the map's Edit toggle
 * (`S.editing`, a durable preference) and the doc bar's own switch (`S.docMode`, per open).
 * They wore the same words and the same class, and the doc bar's consumers were split between
 * them — the editor, Save and the revision log read the bar's flag, the ownership box and the
 * re-slug button read the map's. So a page opened from a view-mode map showed no workflow
 * chip, the bar's switch could not bring it back, and the top bar that could was under the
 * overlay. The bug was not two flags; it was gates asking a raw flag instead of a question.
 *
 * There is ONE flag now — `editing` — and these predicates are the only way a feature may ask
 * about it (`mode.test.ts` scans `features/` and `components/` and fails on a raw read outside
 * the map region and the top bar, which ARE the map's edit mode). The doc bar's switch and the
 * top bar's toggle are the same control: both call `toggleEditing`.
 *
 * What is DERIVED from the flag, rather than stored beside it:
 *  - `docEditorActive`: the Tiptap editor is mounted — edit mode is on, a page is open, and
 *    this user may edit THIS page in the CURRENT locale (`canEditDoc`, the per-document scope
 *    V0.37 introduced). A participant in edit mode on a page that is not theirs sees the
 *    reader with the working chrome around it.
 *  - `docCreateShape`: the bare-node shape of the same thing — edit mode on a node with no
 *    page, for somebody who may create one (`EDIT_MAP`, because creation from the band wires a
 *    slot).
 *
 * The URL's `/edit` segment and the presence socket's "editing" both report `docEditorActive`
 * — what is actually on screen — and never the flag itself.
 */

/** The slice of state the predicates read. `AppSnapshot` and the live `S` both satisfy it. */
export interface ModeState extends ResponsibilityState {
  editing: boolean;
  docOpen: boolean;
  docCurrentId: string | null;
}

/** Is edit mode on? The one flag, behind the one name every feature gate uses. */
export const inEditMode = (s: { editing: boolean }): boolean => s.editing;

/** The document editor is on screen: edit mode, a page, and the right to edit it here. */
export function docEditorActive(s: ModeState): boolean {
  return s.editing && s.docOpen && !!s.docCurrentId && canEditDoc(s, s.docCurrentId, s.currentLocale);
}

/** The bare-node edit shape: edit mode on a node with no page, for somebody who may add one. */
export function docCreateShape(s: ModeState): boolean {
  return s.editing && s.docOpen && !s.docCurrentId && canEditMap(s.perm);
}

/** Either edit shape of the doc view — what the band and the stand-in draw from. */
export const docEditShape = (s: ModeState): boolean => docEditorActive(s) || docCreateShape(s);
